<script setup lang="ts">
import { ref, computed } from 'vue'
import { healthService, type HealthEvent } from '@/api/health'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import {
  faStethoscope,
  faFileMedicalAlt,
  faSkullCrossbones,
  faCalendarDay,
  faCalendarCheck,
  faNotesMedical,
  faSave,
  faTimes,
  faChevronDown,
  faCheckCircle,
  faCircleExclamation,
  faCircleNotch,
  faBriefcaseMedical,
  faHeartPulse
} from '@fortawesome/free-solid-svg-icons'

library.add(
  faStethoscope,
  faFileMedicalAlt,
  faSkullCrossbones,
  faCalendarDay,
  faCalendarCheck,
  faNotesMedical,
  faSave,
  faTimes,
  faChevronDown,
  faCheckCircle,
  faCircleExclamation,
  faCircleNotch,
  faBriefcaseMedical,
  faHeartPulse
)

interface Animal {
  id: string
  species: string
  breed?: string
}

const props = defineProps<{
  animal: Animal
  healthEvent?: HealthEvent | null
}>()

const emit = defineEmits<{
  success: []
}>()

const loading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')

const formData = ref({
  condition: props.healthEvent?.condition || '',
  description: props.healthEvent?.description || '',
  severity: props.healthEvent?.severity || 'Moderate',
  start_date: props.healthEvent?.start_date || new Date().toISOString().split('T')[0],
  end_date: props.healthEvent?.end_date || '',
  status: props.healthEvent?.status || 'Ongoing'
})

const severityOptions = ['Mild', 'Moderate', 'Severe', 'Critical']
const statusOptions = ['Ongoing', 'Recovered', 'Chronic', 'Died']

const isOngoing = computed(() => formData.value.status === 'Ongoing')

async function submitForm() {
  loading.value = true
  errorMessage.value = ''
  successMessage.value = ''

  try {
    const payload = {
      ...formData.value,
      animal: props.animal.id,
      end_date: isOngoing.value ? undefined : formData.value.end_date
    }

    if (props.healthEvent?.id) {
      await healthService.updateHealthEvent(props.healthEvent.id, payload)
      successMessage.value = 'Medical record updated successfully!'
    } else {
      await healthService.createHealthEvent(payload)
      successMessage.value = 'Health event recorded successfully!'
    }
    
    setTimeout(() => {
      emit('success')
    }, 1500)
  } catch (err: any) {
    errorMessage.value = err.response?.data?.detail || err.message || 'Failed to save medical event'
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <form @submit.prevent="submitForm" class="space-y-6">
    <!-- Medical Diagnosis Section -->
    <div class="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
      <div class="p-6 border-b border-slate-50 bg-slate-50/50">
        <h3 class="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
          <div class="w-1.5 h-6 bg-blue-500 rounded-full"></div>
          Clinical Diagnosis
        </h3>
      </div>
      
      <div class="p-8 space-y-8">
        <!-- Condition -->
        <div class="space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Condition / Disease <span class="text-rose-500">*</span></label>
          <div class="relative group">
            <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-blue-500 transition-colors">
              <FontAwesomeIcon icon="stethoscope" />
            </div>
            <input 
              v-model="formData.condition" 
              type="text" 
              required 
              placeholder="e.g. Bovine Respiratory Disease"
              class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 text-slate-700 font-bold transition-all"
            >
          </div>
        </div>

        <!-- Description -->
        <div class="space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Detailed Observations</label>
          <div class="relative group">
            <div class="absolute top-4 left-4 pointer-events-none text-slate-300 group-focus-within:text-blue-500 transition-colors">
              <FontAwesomeIcon icon="file-medical-alt" />
            </div>
            <textarea 
              v-model="formData.description" 
              rows="3" 
              placeholder="Describe symptoms, behavioral changes, or physical findings..." 
              class="block w-full pl-11 pr-4 py-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 text-slate-700 font-bold transition-all"
            ></textarea>
          </div>
        </div>
      </div>
    </div>

    <!-- Severity & Status Selection -->
    <div class="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
      <div class="p-6 border-b border-slate-50 bg-slate-50/50">
        <h3 class="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
          <div class="w-1.5 h-6 bg-indigo-500 rounded-full"></div>
          Acuity & Resolution
        </h3>
      </div>

      <div class="p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
        <!-- Severity -->
        <div class="space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Acuity Level <span class="text-rose-500">*</span></label>
          <div class="relative group">
            <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-indigo-500 transition-colors">
              <FontAwesomeIcon icon="briefcase-medical" />
            </div>
            <select 
              v-model="formData.severity" 
              required
              class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 text-slate-700 font-bold transition-all appearance-none cursor-pointer"
            >
              <option v-for="severity in severityOptions" :key="severity" :value="severity">{{ severity }}</option>
            </select>
            <div class="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-slate-300">
              <FontAwesomeIcon icon="chevron-down" class="text-xs" />
            </div>
          </div>
        </div>

        <!-- Status -->
        <div class="space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Prognosis/Status <span class="text-rose-500">*</span></label>
          <div class="relative group">
            <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-indigo-500 transition-colors">
              <FontAwesomeIcon icon="heart-pulse" />
            </div>
            <select 
              v-model="formData.status" 
              required
              class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 text-slate-700 font-bold transition-all appearance-none cursor-pointer"
            >
              <option v-for="status in statusOptions" :key="status" :value="status">{{ status }}</option>
            </select>
            <div class="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-slate-300">
              <FontAwesomeIcon icon="chevron-down" class="text-xs" />
            </div>
          </div>
        </div>

        <!-- Start Date -->
        <div class="space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Onset Date <span class="text-rose-500">*</span></label>
          <div class="relative group">
            <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-emerald-500 transition-colors">
              <FontAwesomeIcon icon="calendar-day" />
            </div>
            <input 
              v-model="formData.start_date" 
              type="date" 
              required 
              class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 text-slate-700 font-bold transition-all"
            >
          </div>
        </div>

        <!-- End Date -->
        <div v-if="!isOngoing" class="space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Resolution Date</label>
          <div class="relative group">
            <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-emerald-500 transition-colors">
              <FontAwesomeIcon icon="calendar-check" />
            </div>
            <input 
              v-model="formData.end_date" 
              type="date" 
              class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 text-slate-700 font-bold transition-all"
            >
          </div>
        </div>
      </div>
    </div>

    <!-- Alert Messages -->
    <div class="space-y-3">
      <transition enter-active-class="transition duration-300 ease-out" enter-from-class="transform -translate-y-4 opacity-0" enter-to-class="transform translate-y-0 opacity-100">
        <div v-if="successMessage" class="bg-emerald-50 border border-emerald-100 text-emerald-700 rounded-2xl p-4 flex items-center shadow-sm font-bold">
          <FontAwesomeIcon icon="check-circle" class="mr-3 text-lg" />
          {{ successMessage }}
        </div>
      </transition>
      
      <transition enter-active-class="transition duration-300 ease-out" enter-from-class="transform -translate-y-4 opacity-0" enter-to-class="transform translate-y-0 opacity-100">
        <div v-if="errorMessage" class="bg-rose-50 border border-rose-100 text-rose-700 rounded-2xl p-4 flex items-center shadow-sm font-bold">
          <FontAwesomeIcon icon="circle-exclamation" class="mr-3 text-lg" />
          {{ errorMessage }}
        </div>
      </transition>
    </div>

    <div class="flex justify-end gap-3 pt-4 border-t border-slate-100">
      <button 
        type="button" 
        @click="$emit('success')" 
        class="px-8 py-3.5 text-slate-400 font-black rounded-2xl hover:bg-slate-50 hover:text-slate-600 transition-all active:scale-95 flex items-center gap-2"
      >
        <FontAwesomeIcon icon="times" />
        Discard
      </button>
      <button 
        type="submit" 
        :disabled="loading"
        class="px-10 py-3.5 bg-slate-900 text-white font-black rounded-2xl hover:bg-slate-800 transition-all shadow-xl shadow-slate-100 active:scale-95 disabled:bg-slate-200 disabled:shadow-none flex items-center gap-3"
      >
        <FontAwesomeIcon v-if="loading" icon="circle-notch" class="animate-spin" />
        <FontAwesomeIcon v-else icon="save" />
        {{ loading ? 'Securing Record...' : (healthEvent?.id ? 'Finalize Changes' : 'Record Health Event') }}
      </button>
    </div>
  </form>
</template>
